import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FolderArchive, Calendar, Send, Download, Settings, FileText, RefreshCw,
  CheckCircle, Clock, AlertCircle, XCircle, MoreHorizontal, Plus, Mail,
  Eye, Link2, ChevronRight, Upload, File, Loader2, Building2, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

// Status config
const statusConfig = {
  in_progress: { label: 'En préparation', color: 'bg-slate-100 text-slate-700', icon: Clock },
  ready: { label: 'Prêt à envoyer', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  sent: { label: 'Envoyé', color: 'bg-green-100 text-green-700', icon: Send },
  downloaded: { label: 'Téléchargé', color: 'bg-emerald-100 text-emerald-800', icon: Download },
  expired: { label: 'Lien expiré', color: 'bg-red-100 text-red-700', icon: XCircle }
};

// Category config
const categoryConfig = {
  sales_invoices: { label: 'Factures ventes', icon: FileText },
  purchase_invoices: { label: 'Factures achats', icon: FileText },
  bank_statements: { label: 'Relevé bancaire', icon: Building2 },
  sepa_remises: { label: 'Remises SEPA', icon: Building2 },
  payslips: { label: 'Bulletins salaire', icon: FileText },
  manual_files: { label: 'Documents divers', icon: Upload }
};

// Category status indicator
const CategoryStatus = ({ expected, actual, enabled }) => {
  if (!enabled) {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  
  if (actual === 0) {
    return (
      <div className="flex items-center gap-1 text-slate-400">
        <span className="text-xs font-medium">0</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1 text-green-600">
      <CheckCircle className="w-3 h-3" />
      <span className="text-xs font-medium">{actual}</span>
    </div>
  );
};

// Current folder card - renamed to SelectedFolderCard
const SelectedFolderCard = ({ folder, isCurrent, onOpen, onSend, onCollect, collecting }) => {
  const stats = folder.stats || {};
  
  // Calculate total expected vs actual
  let totalExpected = 0;
  let totalActual = 0;
  
  Object.entries(stats).forEach(([key, stat]) => {
    if (stat.enabled !== false) {
      totalExpected += stat.expected || 0;
      totalActual += stat.actual || 0;
    }
  });
  
  const progress = totalExpected > 0 ? (totalActual / totalExpected) * 100 : 0;
  
  // Format send date
  const sendDate = folder.send_date ? new Date(folder.send_date) : null;
  const sendDateStr = sendDate 
    ? sendDate.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
    : 'Non défini';
  
  const status = statusConfig[folder.status] || statusConfig.in_progress;
  const StatusIcon = status.icon;
  
  return (
    <Card className={`border-2 ${isCurrent ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' : 'border-slate-200 bg-white'} mb-6`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${isCurrent ? 'bg-blue-100' : 'bg-slate-100'} rounded-lg`}>
              <FolderArchive className={`w-6 h-6 ${isCurrent ? 'text-blue-600' : 'text-slate-600'}`} />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                Dossier {folder.display_name}
                {isCurrent ? (
                  <Badge className="bg-blue-100 text-blue-700 font-normal">En cours de constitution</Badge>
                ) : (
                  <Badge className={status.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {status.label}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription className="flex items-center gap-1 mt-1">
                <Calendar className="w-3 h-3" />
                {folder.sent_at 
                  ? `Envoyé le ${new Date(folder.sent_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}`
                  : `Envoi prévu le ${sendDateStr}`
                }
              </CardDescription>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCollect} disabled={collecting}>
              {collecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Collecter
            </Button>
            <Button variant="outline" size="sm" onClick={onOpen}>
              <Eye className="w-4 h-4 mr-1" />
              Ouvrir
            </Button>
            <Button size="sm" onClick={onSend} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-1" />
              {folder.status === 'sent' ? 'Renvoyer' : 'Envoyer'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-600">{totalActual} fichiers collectés</span>
          </div>
          <Progress value={Math.min(100, totalActual > 0 ? 100 : 0)} className="h-2" />
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Object.entries(categoryConfig).map(([key, config]) => {
            const stat = stats[key] || {};
            const Icon = config.icon;
            
            return (
              <div key={key} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-700">{config.label}</span>
                </div>
                <CategoryStatus expected={stat.expected || 0} actual={stat.actual || 0} enabled={stat.enabled !== false} />
              </div>
            );
          })}
        </div>
        
        {folder.updated_at && (
          <p className="text-xs text-slate-400 mt-4">
            Dernière mise à jour : {new Date(folder.updated_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Folder list item
const FolderListItem = ({ folder, onOpen, onAction }) => {
  const status = statusConfig[folder.status] || statusConfig.in_progress;
  const StatusIcon = status.icon;
  
  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-slate-300 transition-colors">
      <div className="flex items-center gap-4">
        <div className="p-2 bg-slate-100 rounded-lg">
          <FolderArchive className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <h3 className="font-medium text-slate-900">Dossier {folder.display_name}</h3>
          <div className="flex items-center gap-3 mt-1">
            <Badge className={status.color}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {status.label}
            </Badge>
            <span className="text-sm text-slate-500">{folder.file_count || 0} fichiers</span>
            {folder.sent_at && (
              <span className="text-xs text-slate-400">
                Envoyé le {new Date(folder.sent_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onOpen(folder.folder_id)}>
          <Eye className="w-4 h-4 mr-1" />
          Voir
        </Button>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAction(folder.folder_id, 'download')}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger le ZIP
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAction(folder.folder_id, 'link')}>
              <Link2 className="w-4 h-4 mr-2" />
              Générer un nouveau lien
            </DropdownMenuItem>
            {folder.status === 'sent' && (
              <DropdownMenuItem onClick={() => onAction(folder.folder_id, 'resend')}>
                <Mail className="w-4 h-4 mr-2" />
                Renvoyer email
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default function Comptabilite() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [filterMonth, setFilterMonth] = useState('current');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const now = new Date();
  const [createMonth, setCreateMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [createYear, setCreateYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const [foldersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/api/comptabilite/folders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/comptabilite/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setFolders(foldersRes.data.folders || []);
      setSettings(settingsRes.data);
      
      // Find current folder
      const current = foldersRes.data.folders.find(f => f.is_current);
      setCurrentFolder(current);
      setSelectedFolder(current); // Par défaut, afficher le dossier en cours
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCollect = async () => {
    if (!selectedFolder) return;
    
    setCollecting(true);
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${selectedFolder.folder_id}/collect`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${res.data.collected_count} fichiers collectés`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la collecte');
    } finally {
      setCollecting(false);
    }
  };

  const handleSend = async () => {
    if (!selectedFolder) return;
    
    if (!settings?.expert_email) {
      toast.error('Configurez d\'abord l\'email de l\'expert comptable');
      navigate('/comptabilite/parametres');
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/${selectedFolder.folder_id}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Dossier envoyé à ${res.data.sent_to.join(', ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handleFilterChange = async (value) => {
    setFilterMonth(value);
    if (value === 'current') {
      setSelectedFolder(currentFolder);
    } else {
      // Charger les détails complets du dossier sélectionné
      try {
        const res = await axios.get(
          `${API}/api/comptabilite/folders/${value}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedFolder(res.data);
      } catch (error) {
        console.error('Error loading folder details:', error);
        // Fallback to basic data from list
        const folder = folders.find(f => f.folder_id === value);
        setSelectedFolder(folder);
      }
    }
  };

  const handleCreateFolder = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/api/comptabilite/folders/create/${createYear}/${createMonth}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.already_exists) {
        toast.info(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      setCreateOpen(false);
      navigate(`/comptabilite/dossier/${res.data.folder_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenFolder = (folderId) => {
    navigate(`/comptabilite/dossier/${folderId}`);
  };

  const handleFolderAction = async (folderId, action) => {
    try {
      switch (action) {
        case 'download':
          window.open(`${API}/api/comptabilite/folders/${folderId}/download?token=${token}`, '_blank');
          break;
        case 'link':
          const res = await axios.post(
            `${API}/api/comptabilite/folders/${folderId}/generate-link`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          navigator.clipboard.writeText(res.data.download_url);
          toast.success('Lien copié dans le presse-papiers');
          break;
        case 'resend':
          await axios.post(
            `${API}/api/comptabilite/folders/${folderId}/send`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          toast.success('Email renvoyé');
          fetchData();
          break;
        default:
          break;
      }
    } catch (error) {
      toast.error('Erreur lors de l\'action');
    }
  };

  // Calculate next send date from settings
  const getNextSendDate = () => {
    if (!settings) return null;
    
    const now = new Date();
    let sendMonth = now.getMonth();
    let sendYear = now.getFullYear();
    let sendDay = settings.send_day || 15;
    
    // If we've passed the send day this month, it's next month
    if (now.getDate() >= sendDay) {
      sendMonth++;
      if (sendMonth > 11) {
        sendMonth = 0;
        sendYear++;
      }
    }
    
    return new Date(sendYear, sendMonth, sendDay, settings.send_hour || 8, 0);
  };

  const nextSendDate = getNextSendDate();

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48" />
        <div className="space-y-3">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FolderArchive className="w-6 h-6 text-blue-500" />
            Dossiers comptables
          </h1>
          {nextSendDate && settings?.auto_send_enabled && (
            <p className="text-slate-500 text-sm mt-1">
              Prochain envoi automatique : le {nextSendDate.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })} à {String(settings.send_hour || 8).padStart(2, '0')}h00
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCreateOpen(true)} data-testid="create-folder-btn">
            <Plus className="w-4 h-4 mr-2" />
            Créer un dossier
          </Button>
          <Button variant="outline" onClick={() => navigate('/comptabilite/parametres')}>
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
        </div>
      </div>

      {/* Warning if no expert email */}
      {!settings?.expert_email && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-amber-800 font-medium">Configuration requise</p>
            <p className="text-amber-700 text-sm">Configurez email de votre expert comptable pour pouvoir envoyer les dossiers.</p>
          </div>
          <Button size="sm" onClick={() => navigate('/comptabilite/parametres')}>
            Configurer
          </Button>
        </div>
      )}

      {/* Month filter */}
      <Card className="bg-slate-50">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Afficher le dossier :</span>
            </div>
            <Select value={filterMonth} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-64 bg-white">
                <SelectValue placeholder="Sélectionner un mois" />
              </SelectTrigger>
              <SelectContent>
                {currentFolder && (
                  <SelectItem value="current">
                    📌 {currentFolder.display_name} (en cours)
                  </SelectItem>
                )}
                {folders.filter(f => !f.is_current).map(folder => (
                  <SelectItem key={folder.folder_id} value={folder.folder_id}>
                    {folder.display_name} {folder.status === 'sent' ? '✓' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Selected folder (shown at top) */}
      {selectedFolder && (
        <SelectedFolderCard
          folder={selectedFolder}
          isCurrent={selectedFolder.is_current}
          onOpen={() => handleOpenFolder(selectedFolder.folder_id)}
          onSend={handleSend}
          onCollect={handleCollect}
          collecting={collecting}
        />
      )}

      {/* Create folder dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Créer un dossier pour un mois précis</DialogTitle>
            <DialogDescription>Le dossier sera créé vide : utilisez ensuite "Collecter" pour rassembler automatiquement les documents du mois.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-2">
              <span className="text-sm font-medium text-slate-700">Mois</span>
              <Select value={String(createMonth)} onValueChange={(v) => setCreateMonth(parseInt(v))}>
                <SelectTrigger data-testid="create-folder-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'].map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <span className="text-sm font-medium text-slate-700">Année</span>
              <Select value={String(createYear)} onValueChange={(v) => setCreateYear(parseInt(v))}>
                <SelectTrigger data-testid="create-folder-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreateFolder} disabled={creating} data-testid="create-folder-confirm-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer le dossier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Past folders */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Historique des dossiers</h2>
        
        {folders.filter(f => !f.is_current).length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border border-dashed">
            <FolderArchive className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Aucun dossier envoyé pour le moment</p>
            <p className="text-slate-400 text-sm mt-1">Les dossiers envoyés apparaîtront ici</p>
          </div>
        ) : (
          <div className="space-y-3">
            {folders.filter(f => !f.is_current).map(folder => (
              <FolderListItem
                key={folder.folder_id}
                folder={folder}
                onOpen={handleOpenFolder}
                onAction={handleFolderAction}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
